import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import "../styles.css";
import bebasFont from "../fonts/bebas-neue-latin.woff2?url";
import interFont from "../fonts/inter-latin.woff2?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { initAnalytics, trackPageView } from "../lib/analytics";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "author", content: "Scott Ranch Sand & Gravel, LLC" },
      { name: "theme-color", content: "#2a1f17" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Scott Ranch Sand & Gravel" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "google-site-verification", content: "UCMW1zICSioqUAnHCu60l8wzQUj4h_HFp13JjVLGPNk" },
      { property: "og:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/H11f8sPnVxbVMPidooAs9ItoKYe2/social-images/social-1781803537786-randy_logo.webp" },
      { property: "og:image:alt", content: "Scott Ranch Sand & Gravel horseshoe logo" },
      { name: "twitter:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/H11f8sPnVxbVMPidooAs9ItoKYe2/social-images/social-1781803537786-randy_logo.webp" },
      { name: "twitter:image:alt", content: "Scott Ranch Sand & Gravel horseshoe logo" },
    ],
    links: [
      {
        rel: "preload",
        href: bebasFont,
        as: "font",
        type: "font/woff2",
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        href: interFont,
        as: "font",
        type: "font/woff2",
        crossOrigin: "anonymous",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "@id": "https://scottranchsandgravel.com/#business",
          name: "Scott Ranch Sand & Gravel, LLC",
          url: "https://scottranchsandgravel.com/",
          description:
            "Family-owned Ellis County excavation contractor. Driveway and culvert install, pool demolition, pond builds, tree removal, and aggregate delivery.",
          slogan: "Dirt work, done right.",
          image: "https://storage.googleapis.com/gpt-engineer-file-uploads/H11f8sPnVxbVMPidooAs9ItoKYe2/social-images/social-1781803537786-randy_logo.webp",
          telephone: "+1-214-616-4452",
          email: "scottranchautosales@gmail.com",
          priceRange: "$$",
          areaServed: { "@type": "AdministrativeArea", name: "Ellis County, Texas" },
          address: {
            "@type": "PostalAddress",
            addressLocality: "Ellis County",
            addressRegion: "TX",
            addressCountry: "US",
          },
          sameAs: [
            "https://www.facebook.com/p/Scott-Ranch-Sand-and-Gravel-100064129207992/",
            "https://share.google/y4z0feBHirVJVIw81",
          ],
          aggregateRating: { "@type": "AggregateRating", ratingValue: "5", reviewCount: "3" },
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: "Excavation services",
            itemListElement: [
              "Driveway Install",
              "Culvert Install",
              "Pool Demolition",
              "Pond Builds & Repairs",
              "Excavation",
              "Tree Removal",
              "Creek Crossings",
              "Aggregate Delivery",
            ].map((name) => ({
              "@type": "Offer",
              itemOffered: { "@type": "Service", name },
            })),
          },
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});


function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
        <noscript>
          <style>{".reveal{opacity:1;transform:none}"}</style>
        </noscript>
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const router = useRouter();

  useEffect(() => {
    initAnalytics();
    const unsubscribe = router.subscribe("onResolved", () => {
      trackPageView(window.location.pathname);
    });
    return () => unsubscribe();
  }, [router]);

  return (
    // Required: nested routes render here. Removing <Outlet /> breaks all child routes.
    <Outlet />
  );
}
